import { useMemo } from 'react';
import { TABLE } from '../../../../../public/js/react/feature/tint-editor/schemas/table.theme';
import {
  IUpdateMessagesPayload,
  selectIsClosedByHighlightPostFeature,
  selectIsPopupClosed,
  selectIsPopupLoaded,
  updateMessages,
} from '../modules/messages';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeNames } from '../components/theme/theme.utils';

export const useThemeConfig = ({ theme, personalization, config }) => {
  const dispatch = useDispatch();
  const isPopupClosed = useSelector(selectIsPopupClosed);
  const isPopupLoaded = useSelector(selectIsPopupLoaded);
  const isClosedByHighlightPostFeature = useSelector(selectIsClosedByHighlightPostFeature);
  const setMessages = (payload: IUpdateMessagesPayload) => dispatch(updateMessages(payload));

  const getInitData = (theme, key) => {
    // Use that fn when adding new settings/props/values for existing elements;
    return theme?.default_settings?.[key];
  };

  const {
    attributes: {
      data: {
        waterfallSpeed,
        waterfallToggle,
        sliderType,
        autoscroll: autoScroll,
        highlightPostsToggle,
        postTransitionSpeed,
        bg_page_image: backgroundPageImage,
        postpadding: postPadding,
        postwidth: postWidth,
        cta_button_color: ctaButtonColor,
        cta_button_show: ctaButtonShow,
        cta_text_color: ctaButtonTextColor,
        font_secondary: fontFamily,
        fontsize_secondary: fontSize,
        fontcolor_post: fontColor,
        time_language: timeLanguage,
        color_namebar_buttons: colorNameBarButtons,
        show_author: showAuthor,
        generic_avatar_background_color: backgroundColor,
        generic_avatar_text_color: textColor,
        image_effect: imageEffect,
        sharebuttons: shareButtons,
        sharebuttonoptions: shareButtonOptions,
        atmosphere_blip_colors: atmosphereBlipColors,
        post_duration: postDuration,
        theme_layout: themeLayout,
        post_background_color: postBackgroundColor,
        text_on_hover: textOnHover,
        slider_is_carousel: sliderIsCarousel,
        columns,
        rows,
        overlay_opacity: overlayOpacity,
        overlay_color: overlayColor,
        post_scale: postScale,
        colors,
        shape,
        gem_highlight_duration: gemHighlightDuration,
        wavedirection: direction,
        sliderpadding: gemPadding,
        sliderwidth: gemWidth,
        gemamount: gemAmount,
        verified_buyer_label: verifiedLabel,
        incentivized_label: incentivizedLabel,

        progress_bar_background_color: progressBarBackgroundColor,
        rating_star_color: starColor,
        progress_bar_value_background_color: progressBarValueBackgroundColor,
        rating_button_clear_filter_text: ratingReviewsClearFilterButtonText = getInitData(
          TABLE,
          'rating_button_clear_filter_text'
        ),
        rating_button_clear_filter_text_color: ratingReviewsClearFilterButtonTextColor,
        rating_button_clear_filter_background_color: ratingReviewsClearFilterButtonBackgroundColor,
        rating_button_clear_filter_border_color: ratingReviewsClearFilterButtonBorderColor,
        rating_button_review_text: ratingReviewsReviewButtonText = getInitData(TABLE, 'rating_button_review_text'),
        rating_button_review_text_color: ratingReviewsReviewButtonTextColor,
        rating_button_review_background_color: ratingReviewsReviewButtonBackgroundColor,
        rating_button_review_border_color: ratingReviewsReviewButtonBorderColor,
        rating_font_color_title: ratingReviewsTitleFontColor,
        rating_font_size_title: ratingReviewsTitleFontSize,
        font_color_author: authorFontColor,
        font_color_timestamp: timestampFontColor,
        font_color_share_controls: shareControlsFontColor,
        font_color_verified_label: verifiedLabelColor,
        font_color_incentivized: incentivizedLabelColor,
        font_color_comment: commentLabelColor,
      },
    },
  } = personalization;

  const waterfallConfig = useMemo(
    () => ({
      waterfallToggle,
      waterfallSpeed,
      highlightPostsToggle,
      postTransitionSpeed,
      isClosedByHighlightPostFeature,
      setIsClosedByHighlightPostFeature: setMessages,
    }),
    [waterfallSpeed, waterfallToggle, highlightPostsToggle, postTransitionSpeed, isClosedByHighlightPostFeature]
  );

  return useMemo(() => {
    switch (theme) {
      case ThemeNames.TABLE:
        return {
          showAuthor,
          fontFamily,
          verifiedLabel,
          incentivizedLabel,
          columns: config.columns,
          postPadding,
          postWidth,
          ctaConfig: {
            ctaButtonColor,
            ctaButtonShow,
            ctaButtonTextColor,
            appendUtmToCta: config.cta_append
              ? `utm_campaign=${config.utm_campaign}&utm_content=${config.utm_content}&utm_term=${config.utm_term}&utm_medium=${config.utm_medium}&utm_source=${config.utm_source}`
              : null,
          },
          noPopup: config.noPopup,
          fontSize,
          fontColor,
          timeLanguage,
          colorNameBarButtons,
          backgroundColor,
          textColor,
          imageEffect,
          shareButtons,
          shareButtonOptions,
          noImage: config.noImages,
          noImagesScaling: config.noImagesScaling,
          waterfallConfig,
          isPopupClosed,
          isPopupLoaded,
          themeLayout,
          postBackgroundColor,
          postDuration,

          ratingReviews: {
            buttonText: ratingReviewsReviewButtonText,
            buttonTextColor: ratingReviewsReviewButtonTextColor,
            buttonBackgroundColor: ratingReviewsReviewButtonBackgroundColor,
            buttonBorderColor: ratingReviewsReviewButtonBorderColor,

            ratingSnapshotAttributes: {
              stars: {
                style: {
                  color: starColor,
                },
              },
              progressBar: {
                style: {
                  backgroundColor: progressBarValueBackgroundColor,
                  color: progressBarBackgroundColor,
                },
              },
              button: {
                text: ratingReviewsClearFilterButtonText,
                style: {
                  color: ratingReviewsClearFilterButtonTextColor,
                  backgroundColor: ratingReviewsClearFilterButtonBackgroundColor,
                  borderColor: ratingReviewsClearFilterButtonBorderColor,
                },
              },
            },
            post: {
              title: {
                style: {
                  fontColor: ratingReviewsTitleFontColor,
                  fontSize: ratingReviewsTitleFontSize,
                },
              },
              author: {
                style: {
                  fontColor: authorFontColor,
                },
              },
              timestamp: {
                style: {
                  fontColor: timestampFontColor,
                },
              },
              verifiedLabel: {
                style: {
                  fontColor: verifiedLabelColor,
                },
              },
              commentLabel: {
                style: {
                  fontColor: commentLabelColor,
                },
              },
              incentivizedLabel: {
                style: {
                  fontColor: incentivizedLabelColor,
                },
              },
              shareControls: {
                style: {
                  fontColor: shareControlsFontColor,
                },
              },
            },
          },
          backgroundPageImage,
        };

      case ThemeNames.NEW_TALL_PHOTO:
        return {
          columns: config.columns,
          postPadding,
          postWidth,
          ctaConfig: {
            ctaButtonColor,
            ctaButtonShow,
            ctaButtonTextColor,
            appendUtmToCta: config.cta_append
              ? `utm_campaign=${config.utm_campaign}&utm_content=${config.utm_content}&utm_term=${config.utm_term}&utm_medium=${config.utm_medium}&utm_source=${config.utm_source}`
              : null,
          },
          textOnHover,
          imageEffect,
          colorNameBarButtons,
          shareButtons,
          shareButtonOptions,
          fontFamily,
          fontSize,
          fontColor,
          waterfallConfig,
          noPopup: config.noPopup,
          isPopupLoaded,
          showAuthor,
          avatarBackgroundColor: backgroundColor,
          textColor,
          backgroundPageImage,
        };

      case ThemeNames.EMAIL_TEMPLATE_COLLAGE:
        return {
          rows,
          overlayOpacity,
          overlayColor,
          postPadding,
          postWidth,
          imageEffect,
          fontFamily,
          fontSize,
          fontColor,
          showAuthor,
          postScale,
          postBackgroundColor,
          backgroundPageImage,
        };

      case ThemeNames.EMAIL_TEMPLATE_GRID:
        return {
          columns,
          rows,
          overlayOpacity,
          overlayColor,
          postPadding,
          postWidth,
          imageEffect,
          fontFamily,
          fontSize,
          fontColor,
          showAuthor,
          postScale,
          postBackgroundColor,
          backgroundPageImage,
        };

      case ThemeNames.NEW_SQUARE_PHOTO:
        return {
          columns: config.columns,
          postPadding,
          postWidth,
          ctaConfig: {
            ctaButtonColor,
            ctaButtonShow,
            ctaButtonTextColor,
            appendUtmToCta: config.cta_append
              ? `utm_campaign=${config.utm_campaign}&utm_content=${config.utm_content}&utm_term=${config.utm_term}&utm_medium=${config.utm_medium}&utm_source=${config.utm_source}`
              : null,
          },
          textOnHover,
          imageEffect,
          colorNameBarButtons,
          shareButtons,
          shareButtonOptions,
          fontFamily,
          fontSize,
          fontColor,
          waterfallConfig,
          noPopup: config.noPopup,
          isPopupLoaded,
          backgroundPageImage,
        };

      case ThemeNames.NEW_ATMOSPHERE:
        return {
          atmosphereBlipColors,
          font: fontFamily,
          fontColor,
          imageEffect,
          postDuration,
          timeLanguage,
          backgroundPageImage,
        };

      case ThemeNames.NEW_SLIDER:
      case ThemeNames.NEW_NEWS:
      default:
        return {
          columns: config.columns,
          postPadding,
          sliderType,
          autoScroll,
          postWidth,
          ctaConfig: {
            ctaButtonColor,
            ctaButtonShow,
            ctaButtonTextColor,
            appendUtmToCta: config.cta_append
              ? `utm_campaign=${config.utm_campaign}&utm_content=${config.utm_content}&utm_term=${config.utm_term}&utm_medium=${config.utm_medium}&utm_source=${config.utm_source}`
              : null,
          },
          noPopup: config.noPopup,
          fontFamily,
          fontSize,
          fontColor,
          timeLanguage,
          colorNameBarButtons,
          showAuthor,
          backgroundColor,
          textColor,
          imageEffect,
          shareButtons,
          shareButtonOptions,
          noImage: config.noImages,
          noImagesScaling: config.noImagesScaling,
          waterfallConfig,
          isPopupClosed,
          isPopupLoaded,
          themeLayout,
          postBackgroundColor,
          postDuration,
          backgroundPageImage,
          colors,
          shape,
          gemHighlightDuration,
          direction,
          gemPadding,
          gemWidth,
          gemAmount,
          sliderIsCarousel,
        };
    }
  }, [theme, personalization, config, isPopupClosed, isPopupLoaded, waterfallConfig]);
};
